import { createSignal, type Accessor } from "solid-js";
import { config } from "@/config/env";
import { recordDebugEvent, isDebugEnabled } from "@/lib/debug";

export interface SessionUser {
  userId: string;
  tenantId: string;
  email: string;
  displayName: string;
}

interface SessionResponse {
  authenticated: boolean;
  user?: SessionUser;
  csrfToken?: string;
}

interface ErrorBody {
  error?: {
    code?: string;
    message?: string;
    correlationId?: string;
    details?: unknown;
  };
}

const [user, setUser] = createSignal<SessionUser | null>(null);
const [loading, setLoading] = createSignal<boolean>(true);
const [error, setError] = createSignal<Error | null>(null);
const [csrfToken, setCsrfToken] = createSignal<string | null>(null);

let initialized = false;

export interface AuthStore {
  user: Accessor<SessionUser | null>;
  loading: Accessor<boolean>;
  error: Accessor<Error | null>;
  isAuthenticated: Accessor<boolean>;
  accessToken: Accessor<string | null>;
  csrfToken: Accessor<string | null>;
}

export const authStore: AuthStore = {
  user,
  loading,
  error,
  isAuthenticated: () => Boolean(user()),
  accessToken: () => null,
  csrfToken,
};

export async function initAuthStore(): Promise<void> {
  if (initialized) return;
  initialized = true;
  if (isDebugEnabled()) {
    recordDebugEvent("auth", "initAuthStore called");
  }
  purgeLegacyOidcStorage();
  await refreshAuthSession();
}

export async function refreshAuthSession(): Promise<void> {
  const url = `${config.apiBaseUrl}/auth/session`;
  const startTime = performance.now();
  if (isDebugEnabled()) {
    recordDebugEvent("auth", "refreshAuthSession started", { url });
  }
  try {
    setLoading(true);
    const res = await fetch(url, {
      credentials: "include",
      headers: { accept: "application/json" },
    });
    const duration = (performance.now() - startTime).toFixed(0);
    if (!res.ok) {
      let body: ErrorBody | undefined;
      try {
        body = (await res.json()) as ErrorBody;
      } catch {
        // ignore parse failures
      }
      const code = body?.error?.code ?? `HTTP_${res.status}`;
      const message = body?.error?.message ?? res.statusText;
      const correlation = body?.error?.correlationId
        ? ` correlationId=${body.error.correlationId}`
        : "";
      if (isDebugEnabled()) {
        recordDebugEvent("auth-error", `Session check failed`, {
          status: res.status,
          code,
          duration,
          correlation,
        });
      }
      throw new Error(`Session check failed at ${url}: ${code} ${message}${correlation}`);
    }
    const body = (await res.json()) as { data?: SessionResponse };
    const session = body.data;
    if (session?.authenticated && session.user) {
      setUser(session.user);
      setCsrfToken(session.csrfToken ?? null);
      if (isDebugEnabled()) {
        recordDebugEvent("auth", "Session authenticated", {
          userId: session.user.userId,
          duration,
          csrf: Boolean(session.csrfToken),
        });
      }
    } else {
      clearAuthState();
      if (isDebugEnabled()) {
        recordDebugEvent("auth", "Session not authenticated", { duration });
      }
    }
    setError(null);
  } catch (err) {
    clearAuthState();
    const message = err instanceof Error ? err.message : String(err);
    if (isDebugEnabled()) {
      recordDebugEvent("auth-error", "refreshAuthSession error", {
        message,
        duration: (performance.now() - startTime).toFixed(0),
      });
    }
    setError(new Error(message.includes(url) ? message : `Session check failed at ${url}: ${message}`));
  } finally {
    setLoading(false);
  }
}

export async function getFreshAccessToken(): Promise<string | null> {
  return null;
}

export function setAuthSessionForLogin(value: {
  user: SessionUser;
  csrfToken: string;
}): void {
  setUser(value.user);
  setCsrfToken(value.csrfToken);
  setError(null);
  setLoading(false);
}

export function clearAuthState(): void {
  setUser(null);
  setCsrfToken(null);
}

function purgeLegacyOidcStorage(): void {
  for (const key of Object.keys(window.localStorage)) {
    if (key.startsWith("oidc.")) window.localStorage.removeItem(key);
  }
}

export function _setSessionForTest(value: SessionUser | null): void {
  setUser(value);
  setCsrfToken(value ? "test-csrf" : null);
  setLoading(false);
}
